import { useState, useEffect } from "react";
import { CheckCircle, AlertCircle, X, Shield, RefreshCw, Users, Plus, Minus } from "lucide-react";

const API = process.env.REACT_APP_BACKEND_URL;

const PLAN_LABELS = {
  basic:    { label: "BASIC – €49/4w",    sporters: 10 },
  pro:      { label: "PRO – €99/4w",      sporters: 30 },
  business: { label: "BUSINESS – €159/4w", sporters: 75 },
};

const STATUS_LABELS = {
  active:          { label: "Actief",              cls: "bg-green-500/15 text-green-400 border-green-500/25" },
  trial:           { label: "Trial",               cls: "bg-blue-500/15 text-blue-400 border-blue-500/25" },
  pending_payment: { label: "Wacht op betaling",   cls: "bg-yellow-500/15 text-yellow-400 border-yellow-500/25" },
  suspended:       { label: "Geblokkeerd",         cls: "bg-red-500/15 text-red-400 border-red-500/25" },
  canceled:        { label: "Opgezegd",            cls: "bg-zinc-500/15 text-zinc-400 border-zinc-500/25" },
  none:            { label: "Geen abonnement",     cls: "bg-zinc-800/50 text-zinc-600 border-zinc-700/25" },
};

function StatusBadge({ status }) {
  const s = STATUS_LABELS[status] || STATUS_LABELS.none;
  return (
    <span className={`px-2 py-0.5 rounded-full text-xs font-bold border ${s.cls}`}>
      {s.label}
    </span>
  );
}

function SportersBar({ used, limit }) {
  const total = limit || 0;
  const pct = total > 0 ? Math.min(100, Math.round((used / total) * 100)) : 0;
  const color = pct >= 90 ? "#ef4444" : pct >= 70 ? "#f59e0b" : "#22c55e";
  return (
    <div className="flex items-center gap-2">
      <div className="w-20 h-1.5 bg-white/8 rounded-full overflow-hidden">
        <div className="h-full rounded-full transition-all" style={{ width: `${pct}%`, background: color }} />
      </div>
      <span className="text-xs font-mono" style={{ color }}>
        {used}/{total}
      </span>
    </div>
  );
}

export default function AbonnementenTab({ token }) {
  const [abonnementen, setAbonnementen] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedCompany, setSelectedCompany] = useState(null);
  const [overridePlan, setOverridePlan] = useState("basic");
  const [overrideStatus, setOverrideStatus] = useState("active");
  const [overrideNotitie, setOverrideNotitie] = useState("");
  const [overrideExtraSporters, setOverrideExtraSporters] = useState(0);
  const [saving, setSaving] = useState(false);
  const [result, setResult] = useState(null);

  const auth = { headers: { Authorization: `Bearer ${token}` } };

  const load = () => {
    setLoading(true);
    fetch(`${API}/api/admin/subscriptions`, auth)
      .then(r => r.json())
      .then(d => { setAbonnementen(Array.isArray(d) ? d : []); setLoading(false); })
      .catch(() => setLoading(false));
  };

  useEffect(() => { load(); }, []);

  const openOverride = (company) => {
    setSelectedCompany(company);
    setOverridePlan(company.plan || "basic");
    setOverrideStatus(company.status === "none" ? "active" : company.status || "active");
    setOverrideNotitie(company.override_notitie || "");
    setOverrideExtraSporters(company.extra_sporters || 0);
    setResult(null);
  };

  const saveOverride = async () => {
    if (!selectedCompany) return;
    setSaving(true);
    setResult(null);
    try {
      const r = await fetch(`${API}/api/admin/companies/${selectedCompany.company_id}/subscription-override`, {
        method: "POST",
        headers: { "Content-Type": "application/json", ...auth.headers },
        body: JSON.stringify({
          plan: overridePlan,
          status: overrideStatus,
          notitie: overrideNotitie,
          extra_sporters: overrideExtraSporters,
        }),
      });
      if (r.ok) {
        setResult({ ok: true });
        load();
        setTimeout(() => setSelectedCompany(null), 1200);
      } else {
        const d = await r.json();
        setResult({ ok: false, msg: d.detail || "Fout bij opslaan" });
      }
    } catch {
      setResult({ ok: false, msg: "Netwerk fout" });
    }
    setSaving(false);
  };

  if (loading) return (
    <div className="flex items-center justify-center py-16">
      <div className="w-6 h-6 border-2 border-[#C9A84C]/30 border-t-[#C9A84C] rounded-full animate-spin" />
    </div>
  );

  const planLimit = PLAN_LABELS[overridePlan]?.sporters || 0;
  const totalCapacity = planLimit + overrideExtraSporters;

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-white font-bold text-xl">Abonnementenbeheer</h2>
          <p className="text-zinc-500 text-sm mt-1">Overzicht van alle bedrijfsabonnementen en sportersgebruik.</p>
        </div>
        <button
          onClick={load}
          className="flex items-center gap-1.5 text-zinc-500 hover:text-white text-sm transition-colors border border-white/8 px-3 py-1.5 rounded-lg hover:border-white/20"
        >
          <RefreshCw size={12} /> Vernieuwen
        </button>
      </div>

      {/* Tabel */}
      <div className="bg-[#111] border border-white/8 rounded-2xl overflow-hidden">
        <table className="w-full text-sm">
          <thead>
            <tr className="border-b border-white/5 text-zinc-500 text-xs font-bold uppercase tracking-wider">
              <th className="text-left px-5 py-3">Bedrijf</th>
              <th className="text-left px-5 py-3">Pakket</th>
              <th className="text-left px-5 py-3">Status</th>
              <th className="text-left px-5 py-3">Sporters</th>
              <th className="text-left px-5 py-3">Extra</th>
              <th className="text-right px-5 py-3">Actie</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-white/4">
            {abonnementen.length === 0 && (
              <tr>
                <td colSpan={6} className="text-center py-12 text-zinc-600">
                  Geen bedrijven gevonden
                </td>
              </tr>
            )}
            {abonnementen.map((a, i) => {
              const total = (a.sporters_limit || 0) + (a.extra_sporters || 0);
              const used = a.sporter_count || 0;
              return (
                <tr key={i} className="hover:bg-white/2 transition-colors">
                  <td className="px-5 py-4">
                    <p className="text-white font-semibold">{a.naam}</p>
                    {a.override && (
                      <span className="text-[10px] px-1.5 py-0.5 rounded-full bg-orange-500/15 text-orange-400 border border-orange-500/25 mt-0.5 inline-block">
                        Handmatig
                      </span>
                    )}
                  </td>
                  <td className="px-5 py-4 text-zinc-400">
                    {a.plan ? (PLAN_LABELS[a.plan]?.label || a.plan) : <span className="text-zinc-600">–</span>}
                  </td>
                  <td className="px-5 py-4">
                    <StatusBadge status={a.status} />
                  </td>
                  <td className="px-5 py-4">
                    {total > 0
                      ? <SportersBar used={used} limit={total} />
                      : <span className="text-zinc-600 text-xs">–</span>}
                  </td>
                  <td className="px-5 py-4 text-zinc-400 text-xs">
                    {a.extra_sporters > 0
                      ? <span className="text-[#C9A84C] font-bold">+{a.extra_sporters}</span>
                      : <span className="text-zinc-700">–</span>}
                  </td>
                  <td className="px-5 py-4 text-right">
                    <button
                      onClick={() => openOverride(a)}
                      data-testid={`override-btn-${a.company_id}`}
                      className="flex items-center gap-1.5 ml-auto px-3 py-1.5 rounded-lg bg-[#C9A84C]/10 border border-[#C9A84C]/25 text-[#C9A84C] text-xs font-bold hover:bg-[#C9A84C]/20 transition-all"
                    >
                      <Shield size={11} /> Aanpassen
                    </button>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      {/* Override Modal */}
      {selectedCompany && (
        <div className="fixed inset-0 bg-black/80 z-50 flex items-center justify-center p-4">
          <div className="bg-[#111] border border-white/10 rounded-2xl p-7 max-w-md w-full">
            <div className="flex items-center justify-between mb-5">
              <div>
                <h3 className="text-white font-bold text-lg">Abonnement aanpassen</h3>
                <p className="text-zinc-500 text-xs mt-0.5">{selectedCompany.naam}</p>
              </div>
              <button onClick={() => setSelectedCompany(null)} className="text-zinc-500 hover:text-white">
                <X size={18} />
              </button>
            </div>

            <div className="space-y-4">
              {/* Pakket */}
              <div>
                <label className="block text-zinc-400 text-xs font-bold uppercase tracking-widest mb-2">Pakket</label>
                <select
                  value={overridePlan}
                  onChange={e => setOverridePlan(e.target.value)}
                  className="w-full bg-[#1a1a1a] border border-white/10 rounded-xl px-4 py-3 text-white text-sm focus:outline-none focus:border-[#C9A84C]/50"
                >
                  <option value="basic">BASIC – €49/4w (max 10 sporters)</option>
                  <option value="pro">PRO – €99/4w (max 30 sporters)</option>
                  <option value="business">BUSINESS – €159/4w (max 75 sporters)</option>
                </select>
              </div>

              {/* Status */}
              <div>
                <label className="block text-zinc-400 text-xs font-bold uppercase tracking-widest mb-2">Status</label>
                <select
                  value={overrideStatus}
                  onChange={e => setOverrideStatus(e.target.value)}
                  className="w-full bg-[#1a1a1a] border border-white/10 rounded-xl px-4 py-3 text-white text-sm focus:outline-none focus:border-[#C9A84C]/50"
                >
                  <option value="trial">Trial (gratis proefperiode)</option>
                  <option value="active">Actief</option>
                  <option value="suspended">Geblokkeerd</option>
                  <option value="canceled">Opgezegd</option>
                </select>
              </div>

              {/* Extra sporters */}
              <div>
                <label className="block text-zinc-400 text-xs font-bold uppercase tracking-widest mb-2">
                  Extra sporters
                </label>
                <div className="flex items-center gap-3">
                  <button
                    onClick={() => setOverrideExtraSporters(v => Math.max(0, v - 1))}
                    className="w-10 h-10 rounded-xl bg-white/5 border border-white/10 text-white hover:bg-white/10 flex items-center justify-center transition-all"
                    data-testid="extra-sporters-minus"
                  >
                    <Minus size={14} />
                  </button>
                  <input
                    type="number"
                    min="0"
                    max="500"
                    value={overrideExtraSporters}
                    onChange={e => setOverrideExtraSporters(Math.max(0, parseInt(e.target.value) || 0))}
                    className="flex-1 bg-[#1a1a1a] border border-white/10 rounded-xl px-4 py-2.5 text-white text-center text-lg font-bold focus:outline-none focus:border-[#C9A84C]/50"
                    data-testid="extra-sporters-input"
                  />
                  <button
                    onClick={() => setOverrideExtraSporters(v => v + 1)}
                    className="w-10 h-10 rounded-xl bg-white/5 border border-white/10 text-white hover:bg-white/10 flex items-center justify-center transition-all"
                    data-testid="extra-sporters-plus"
                  >
                    <Plus size={14} />
                  </button>
                </div>
                {/* Totaaloverzicht */}
                <div className="mt-2 flex items-center justify-between text-xs text-zinc-500">
                  <span className="flex items-center gap-1">
                    <Users size={10} />
                    Totale capaciteit: <strong className="text-white ml-1">{totalCapacity} sporters</strong>
                  </span>
                  {overrideExtraSporters > 0 && (
                    <span className="text-[#C9A84C]">
                      +€{(overrideExtraSporters * 2).toFixed(2)}/4w extra
                    </span>
                  )}
                </div>
              </div>

              {/* Notitie */}
              <div>
                <label className="block text-zinc-400 text-xs font-bold uppercase tracking-widest mb-2">Notitie (optioneel)</label>
                <input
                  value={overrideNotitie}
                  onChange={e => setOverrideNotitie(e.target.value)}
                  placeholder="bijv. proefperiode t/m 01-06-2026"
                  className="w-full bg-[#1a1a1a] border border-white/10 rounded-xl px-4 py-3 text-white text-sm focus:outline-none focus:border-[#C9A84C]/50"
                />
              </div>
            </div>

            {result && (
              <div className={`mt-4 p-3 rounded-xl text-sm flex items-center gap-2 ${result.ok ? "bg-green-500/10 border border-green-500/20 text-green-400" : "bg-red-500/10 border border-red-500/20 text-red-400"}`}>
                {result.ok ? <CheckCircle size={14} /> : <AlertCircle size={14} />}
                {result.ok ? "Abonnement bijgewerkt!" : result.msg}
              </div>
            )}

            <div className="flex gap-3 mt-6">
              <button
                onClick={() => setSelectedCompany(null)}
                className="flex-1 py-3 rounded-xl text-sm font-bold border border-white/8 text-zinc-400 hover:text-white"
              >
                Annuleren
              </button>
              <button
                onClick={saveOverride}
                disabled={saving}
                data-testid="save-override-btn"
                className="flex-1 py-3 rounded-xl bg-[#C9A84C] text-black text-sm font-bold hover:bg-[#d4b05c] disabled:opacity-60"
              >
                {saving ? "Opslaan..." : "Opslaan"}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
